/**
 * MiniMetric — denser sibling of :class:`KpiCard` for in-tab metric
 * strips. KpiCard is the hero (22px number, padded), MiniMetric is for
 * 4-6 secondary numbers in a single row.
 *
 *   ┌───────────────────┐
 *   │ LABEL UPPER       │
 *   │ €12.450 ↗ +8%     │
 *   │ contesto opzionale│
 *   └───────────────────┘
 */

import type * as React from "react";

import { cn } from "@ando/ui";


export interface MiniMetricProps {
  readonly label: string;
  readonly value: React.ReactNode;
  /** Optional small footer line — context, delta, trend. */
  readonly sub?: React.ReactNode;
  /** Optional tone applied to the value (success/warn/danger/accent). */
  readonly tone?: "neutral" | "success" | "warn" | "danger" | "accent";
  readonly className?: string;
}


// Pencil (Jul 2026): ~90% neutral. Values stay quiet ink by default; the
// warm cues (emerald/amber/rose) are DESATURATED and reserved for genuinely
// semantic deltas — no cold blue/sky/violet anywhere.
const TONE_TEXT: Readonly<Record<NonNullable<MiniMetricProps["tone"]>, string>> = {
  neutral: "text-ink-1",
  success: "text-emerald-700",
  warn: "text-amber-700",
  danger: "text-rose-700",
  accent: "text-ando-orange",
};


export const MiniMetric: React.FC<MiniMetricProps> = ({
  label,
  value,
  sub,
  tone = "neutral",
  className,
}) => (
  <div
    className={cn(
      // Warm hairline-outlined cell (paper on cream), not a chunky white chip.
      "flex flex-col gap-1 rounded-lg border border-hairline bg-cream/40 px-3 py-2.5",
      className,
    )}
  >
    <span className="font-semibold text-[9.5px] text-ink-3 uppercase tracking-[0.08em]">
      {label}
    </span>
    <span
      className={cn(
        "font-[550] text-[16px] tabular-nums tracking-[-0.02em]",
        TONE_TEXT[tone],
      )}
    >
      {value}
    </span>
    {sub ? (
      <span className="text-[10.5px] text-ink-3 tabular-nums">{sub}</span>
    ) : null}
  </div>
);
MiniMetric.displayName = "MiniMetric";
